import type {
  DecorationInstance,
  InventoryItem,
  InventoryItemType,
  InventoryState,
} from "../types";
import type { GardenGrid } from "../entities/GardenGrid";
import { DECORATION_DEFINITIONS } from "../entities/Decoration";

export interface ShopItem {
  id: string;
  displayName: string;
  type: InventoryItemType;
  price: number;
  sellPrice?: number;
  speciesId?: string;
  catalogId?: string;
  quantity?: number;
  description: string;
  emoji: string;
}
export interface Transaction {
  ok: boolean;
  message: string;
  balance: number;
  item?: ShopItem;
}
type Listener = (inventory: Readonly<InventoryState>) => void;

export class EconomySystem {
  private inventory: InventoryState;
  private readonly listeners = new Set<Listener>();
  constructor(initial?: Partial<InventoryState>, private grid?: GardenGrid) {
    this.inventory = {
      greenPoints: initial?.greenPoints ?? 250,
      items: initial?.items?.map((i) => ({ ...i })) ?? [],
      seeds: { tulip: 3, herb: 2, ...initial?.seeds },
      decorations:
        initial?.decorations?.map((d) => ({
          ...d,
          gridPosition: { ...d.gridPosition },
        })) ?? [],
      toolUpgrades: [...(initial?.toolUpgrades ?? [])],
      ownedDecorationCatalogIds: [
        ...(initial?.ownedDecorationCatalogIds ?? []),
      ],
    };
  }
  get snapshot(): Readonly<InventoryState> {
    return this.inventory;
  }
  owns(itemId: string): boolean {
    return this.inventory.items.some((item) => item.id === itemId && item.quantity > 0);
  }
  ownsExpansion(size: 8 | 10 | 12): boolean {
    return this.owns(`garden-expansion-${size}`);
  }
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  addGP(amount: number): number {
    if (!Number.isFinite(amount) || amount < 0)
      throw new RangeError("GP加算額が不正です。");
    this.inventory.greenPoints += Math.floor(amount);
    this.emit();
    return this.inventory.greenPoints;
  }
  spendGP(amount: number): boolean {
    const n = Math.ceil(amount);
    if (n < 0 || this.inventory.greenPoints < n) return false;
    this.inventory.greenPoints -= n;
    this.emit();
    return true;
  }
  buy(item: ShopItem): Transaction {
    if (item.id.startsWith("garden-expansion-") && this.owns(item.id))
      return { ok: false, message: "この拡張許可証は購入済みです。", balance: this.inventory.greenPoints };
    if (item.price < 0 || !this.spendGP(item.price))
      return {
        ok: false,
        message: "GPが足りません。",
        balance: this.inventory.greenPoints,
      };
    const q = item.quantity ?? 1;
    if (item.type === "seed" || item.type === "seedling") {
      const id = item.speciesId ?? item.id;
      this.inventory.seeds[id] = (this.inventory.seeds[id] ?? 0) + q;
    } else if (item.type === "decoration") {
      const id = item.catalogId ?? item.id;
      if (!this.inventory.ownedDecorationCatalogIds.includes(id))
        this.inventory.ownedDecorationCatalogIds.push(id);
      this.addItem({ ...item, catalogId: id }, q);
    } else if (item.type === "toolUpgrade") {
      if (!this.inventory.toolUpgrades.includes(item.id))
        this.inventory.toolUpgrades.push(item.id);
    } else this.addItem(item, q);
    this.emit();
    return {
      ok: true,
      message: `${item.displayName}を購入しました。`,
      balance: this.inventory.greenPoints,
      item,
    };
  }
  sell(itemId: string, quantity = 1, unitPrice?: number): Transaction {
    const item = this.inventory.items.find((i) => i.id === itemId);
    if (!item || quantity < 1 || item.quantity < quantity)
      return {
        ok: false,
        message: "売却するアイテムが足りません。",
        balance: this.inventory.greenPoints,
      };
    const value = Math.max(
      1,
      Math.floor(unitPrice ?? Number(item.metadata?.sellPrice ?? 5)),
    );
    item.quantity -= quantity;
    if (item.quantity === 0)
      this.inventory.items = this.inventory.items.filter((i) => i !== item);
    this.inventory.greenPoints += value * quantity;
    this.emit();
    return {
      ok: true,
      message: `${item.displayName}を売却しました。`,
      balance: this.inventory.greenPoints,
    };
  }
  consumeItem(itemId: string, quantity = 1): boolean {
    const item = this.inventory.items.find((i) => i.id === itemId);
    if (!item || quantity < 1 || item.quantity < quantity) return false;
    item.quantity -= quantity;
    if (item.quantity === 0)
      this.inventory.items = this.inventory.items.filter((i) => i !== item);
    this.emit();
    return true;
  }
  consumeSeed(speciesId: string, quantity = 1): boolean {
    const count = this.inventory.seeds[speciesId] ?? 0;
    if (count < quantity) return false;
    this.inventory.seeds[speciesId] = count - quantity;
    this.emit();
    return true;
  }
  addSeed(speciesId: string, quantity = 1): void {
    this.inventory.seeds[speciesId] =
      (this.inventory.seeds[speciesId] ?? 0) +
      Math.max(0, Math.floor(quantity));
    this.emit();
  }
  addDecoration(instance: DecorationInstance): void {
    this.inventory.decorations.push(instance);
    this.emit();
  }
  removeDecoration(instanceId: string): DecorationInstance | undefined {
    const i = this.inventory.decorations.findIndex(
      (d) => d.instanceId === instanceId,
    );
    if (i < 0) return undefined;
    const [removed] = this.inventory.decorations.splice(i, 1);
    if (!removed) return undefined;
    const item = this.inventory.items.find(
      (candidate) => candidate.catalogId === removed.decorationId,
    );
    if (item) item.quantity += 1;
    else {
      const definition = DECORATION_DEFINITIONS[removed.decorationId as keyof typeof DECORATION_DEFINITIONS];
      this.inventory.items.push({
        id: removed.decorationId,
        type: "decoration",
        quantity: 1,
        displayName: definition?.displayName ?? removed.decorationId,
        catalogId: removed.decorationId,
        ...(definition ? { metadata: { sellPrice: Math.max(1, Math.floor(definition.priceGP * 0.45)) } } : {}),
      });
    }
    this.emit();
    return removed;
  }
  placeDecoration(
    catalogId: string,
    gridPosition: { x: number; y: number },
    rotation: 0 | 90 | 180 | 270 = 0,
  ): boolean {
    const definition = DECORATION_DEFINITIONS[catalogId as keyof typeof DECORATION_DEFINITIONS];
    const item = this.inventory.items.find(
      (i) => i.catalogId === catalogId && i.quantity > 0,
    );
    if (!item || !definition || !this.grid?.canPlace(gridPosition, definition.footprint, "decoration"))
      return false;
    const instance: import("../types").DecorationInstance = {
      instanceId: crypto.randomUUID() as import("../types").DecorationInstanceId,
      decorationId: catalogId,
      gridPosition: { x: gridPosition.x, y: gridPosition.y },
      rotation,
    };
    if (!this.grid.placeDecoration(instance, definition.footprint)) return false;
    item.quantity -= 1;
    if (item.quantity === 0)
      this.inventory.items = this.inventory.items.filter((i) => i !== item);
    this.inventory.decorations.push(instance);
    this.emit();
    return true;
  }
  addRewardItem(id: string, quantity = 1): void {
    this.addItem(
      {
        id,
        displayName: id,
        type: "consumable",
        price: 0,
        description: "クエスト報酬",
        emoji: "🎁",
      },
      quantity,
    );
    this.emit();
  }
  serialize(): InventoryState {
    return {
      greenPoints: this.inventory.greenPoints,
      items: this.inventory.items.map((i) =>
        i.metadata ? { ...i, metadata: { ...i.metadata } } : { ...i },
      ),
      seeds: { ...this.inventory.seeds },
      decorations: this.inventory.decorations.map((d) => ({
        ...d,
        gridPosition: { ...d.gridPosition },
      })),
      toolUpgrades: [...this.inventory.toolUpgrades],
      ownedDecorationCatalogIds: [...this.inventory.ownedDecorationCatalogIds],
    };
  }
  private addItem(item: ShopItem, quantity: number) {
    const old = this.inventory.items.find((i) => i.id === item.id);
    if (old) old.quantity += quantity;
    else {
      const entry: InventoryItem = {
        id: item.id,
        type: item.type,
        quantity,
        displayName: item.displayName,
      };
      if (item.speciesId !== undefined) entry.speciesId = item.speciesId;
      if (item.catalogId !== undefined) entry.catalogId = item.catalogId;
      entry.metadata = {
        sellPrice: item.sellPrice ?? Math.max(1, Math.floor(item.price * 0.45)),
      };
      this.inventory.items.push(entry);
    }
  }
  private emit() {
    for (const fn of this.listeners) fn(this.snapshot);
  }
}
